import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import providerService from '../services/providerService';
import userService from '../services/userService';
import providersFetchedAction from '../actions/providersFetchedAction';

const useProvidersList = (options = {}) => {
  const { skipInitialFetch = false } = options;
  const userCountry = localStorage.getItem('userCountry');
  const { professionalId } = userService.getUserDetails();
  const {
    getProviderListBackend,
    storeProviderDetails,
    updateProviderDetails,
  } = providerService;
  const dispatch = useDispatch();
  const providersFetchedFlag = useSelector(
    (state) => state.providersFetched,
  );

  const fetchAndStoreProvidersList = useCallback(async () => {
    try {
      dispatch(providersFetchedAction('FETCHING'));
      const providerList = await getProviderListBackend(
        professionalId,
        userCountry,
      );

      if (
        providerList.providers &&
        providerList.providers.length > 0
      ) {
        dispatch(providersFetchedAction('FETCHED'));

        // Check if 'cpd' already exists in localStorage directly
        const existingCpd = localStorage.getItem('cpd');

        if (existingCpd) {
          // Update existing 'cpd' entry with new provider list
          updateProviderDetails('providerList', providerList);
        } else {
          // Create new 'cpd' entry if it doesn't exist
          const providerId = providerList.providers[0]?.id;
          const countryCode = userCountry;
          storeProviderDetails(providerId, providerList, countryCode);
        }
      } else {
        // no clinic. Do not fetch anymore
        dispatch(providersFetchedAction('NOFETCH'));
      }
      return null;
    } catch (error) {
      dispatch(providersFetchedAction('NONE'));
      console.error('Failed to fetch providers:', error);
      return null;
    }
  }, [
    dispatch,
    getProviderListBackend,
    professionalId,
    userCountry,
    storeProviderDetails,
    updateProviderDetails,
  ]);

  // Run effect to fetch providers if needed
  // Flag updates between NONE, FETCHING, FETCHED, NOFETCH (redux state to prevent simultaneous fetches)
  // 1. providersFetchedFlag is 'NONE' (initial state or after error)
  // 2. 'cpd' doesn't exist in localStorage AND flag is not 'FETCHING' or 'NOFETCH'
  //    This ensures we refetch if localStorage was cleared but prevents infinite fetching
  useEffect(() => {
    if (skipInitialFetch) return;

    if (
      providersFetchedFlag === 'NONE' ||
      (!localStorage.getItem('cpd') &&
        !['FETCHING', 'NOFETCH'].includes(providersFetchedFlag))
    ) {
      fetchAndStoreProvidersList();
    }
  }, [
    providersFetchedFlag,
    skipInitialFetch,
    fetchAndStoreProvidersList,
  ]);

  // Return the fetch function so other components can trigger a refetch
  return { refetch: fetchAndStoreProvidersList };
};

export default useProvidersList;
